/**
 * Pitch bot.
 *
 * Works from the bot's own view only:
 *  - Bidding: estimates how many of the four points (High, Low, Jack, Game) a candidate trump suit should bring in and
 *    bids that many, clamped to the legal range; the difficulty's aggression shifts the estimate.
 *  - Pitching: the bidder leads the strongest card of the best suit, which makes that suit trump.
 *  - Leading: the bidding side draws trump while it holds the master; defenders cash side-suit aces.
 *  - Following: feed points to a winning partner, win as cheaply as possible against opponents and otherwise shed the
 *    cheapest card, keeping the low trump (a point in its own right). HARD/EXPERT/NORMAL track played cards.
 */
import { buildDeck, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  pitchGameValue,
  pitchPartnerOf,
  pitchTrickWinner,
  type PitchAction,
  type PitchLegal,
  type PitchTrickPlay,
  type PitchView,
} from './engine.js';

const suitOf = (id: CardId) => parseCard(id).suit;
const rankOf = (id: CardId) => parseCard(id).rank;
const SUIT_LIST: Suit[] = ['S', 'H', 'D', 'C'];

/** Rough number of the four points a hand should win with `suit` as trump (higher = better). */
export function pitchHandStrength(cards: readonly CardId[], suit: Suit): number {
  const trumps = cards.filter((c) => suitOf(c) === suit).map(rankOf);
  let est = 0;
  if (trumps.length > 0) {
    if (trumps.includes(14)) est += 1; // High
    else if (trumps.includes(13)) est += 0.4;
    if (Math.min(...trumps) <= 4) est += 0.8; // Low
    else if (Math.min(...trumps) <= 6) est += 0.4;
    if (trumps.includes(11)) est += 0.8; // Jack
    if (trumps.length >= 3) est += (trumps.length - 2) * 0.4; // length wins tricks, so Game
  }
  // Game: tens, aces and kings in the hand.
  for (const c of cards) est += pitchGameValue(c) >= 10 ? 0.25 : rankOf(c) === 14 ? 0.15 : 0;
  return est;
}

function bestSuit(cards: readonly CardId[]): { suit: Suit; score: number } {
  let best = { suit: SUIT_LIST[0]!, score: -1 };
  for (const suit of SUIT_LIST) {
    const score = pitchHandStrength(cards, suit);
    if (score > best.score) best = { suit, score };
  }
  return best;
}

function bidChoice(view: PitchView, legal: Extract<PitchLegal, { kind: 'bid' }>, ctx: BotContext): PitchAction {
  const est = bestSuit(view.hand).score + (ctx.profile.aggression - 0.5) * 1.2;
  const target = Math.min(4, Math.max(2, Math.round(est)));
  const options = legal.amounts.filter((a) => a <= target);
  if (est >= 1.7 && options.length > 0) return { type: 'bid', amount: options.at(-1)! };
  if (!legal.canPass) return { type: 'bid', amount: legal.amounts[0]! };
  return { type: 'pass' };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: PitchView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const remembered = ctx.profile.memory;
  view.tricks.forEach((t, i) => {
    // Forgetful bots only recall the most recent tricks.
    if (remembered >= 1 || i >= view.tricks.length - Math.max(1, Math.round(view.tricks.length * remembered))) {
      for (const p of t.plays) seen.add(p.card);
    }
  });
  return { unseen: new Set(buildDeck().filter((c) => !seen.has(c))) };
}

function isMaster(card: CardId, k: Knowledge): boolean {
  const s = suitOf(card);
  const mine = rankOf(card);
  for (const u of k.unseen) if (suitOf(u) === s && rankOf(u) > mine) return false;
  return true;
}

const cost = (c: CardId, trump: Suit | null) => pitchGameValue(c) * 10 + rankOf(c) + (suitOf(c) === trump ? 60 : 0);
const cheapest = (cards: readonly CardId[], trump: Suit | null) => [...cards].sort((a, b) => cost(a, trump) - cost(b, trump))[0]!;
const richest = (cards: readonly CardId[]) => [...cards].sort((a, b) => pitchGameValue(b) - pitchGameValue(a) || rankOf(a) - rankOf(b))[0]!;

function wouldWin(trick: readonly PitchTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return pitchTrickWinner([...trick, { seat, card }], trump) === seat;
}

function lead(view: PitchView, legal: CardId[], k: Knowledge | null): CardId {
  const trump = view.trump!;
  const me = view.seat!;
  const trumps = legal.filter((c) => suitOf(c) === trump);
  const side = legal.filter((c) => suitOf(c) !== trump);
  const weBid = view.bidder !== null && view.bidder % 2 === me % 2;

  // The bidding side draws the opponents' trumps while it holds the master.
  if (weBid && trumps.length > 0) {
    const top = [...trumps].sort((a, b) => rankOf(b) - rankOf(a))[0]!;
    if (k ? isMaster(top, k) : rankOf(top) === 14) return top;
  }
  // Cash a master card in a side suit.
  if (k) {
    const masters = side.filter((c) => isMaster(c, k));
    if (masters.length) return richest(masters);
  } else {
    const aces = side.filter((c) => rankOf(c) === 14);
    if (aces.length) return aces[0]!;
  }
  if (side.length) return cheapest(side, trump);
  return cheapest(trumps.length ? trumps : legal, trump);
}

function follow(view: PitchView, legal: CardId[], k: Knowledge | null): CardId {
  const me = view.seat!;
  const trump = view.trump!;
  const trick = view.trick;
  const partner = pitchPartnerOf(me);
  const winnerSeat = pitchTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const last = trick.length === 3;
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));

  if (winnerSeat === partner) {
    // Partner is winning: add points when they are safe, otherwise keep the strong cards.
    const safe = last || (k ? isMaster(winnerCard, k) : rankOf(winnerCard) === 14);
    return safe ? richest(legal) : cheapest(legal, trump);
  }
  if (winners.length > 0) {
    const onTable = trick.reduce((n, p) => n + pitchGameValue(p.card), 0);
    // Do not spend a trump on an empty trick unless it is the last card.
    const plain = winners.filter((c) => suitOf(c) !== trump || last || onTable >= 4 || trick.some((p) => p.card === `J${trump}`));
    const pool = plain.length ? plain : winners;
    return cheapest(pool, trump);
  }
  return cheapest(legal, trump);
}

export function pitchBotChoose(view: PitchView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  // Pitching: the bidder leads the top card of the best suit, which becomes trump.
  if (view.trump === null) {
    const { suit } = bestSuit(view.hand);
    const ofSuit = legal.filter((c) => suitOf(c) === suit);
    return [...(ofSuit.length ? ofSuit : legal)].sort((a, b) => rankOf(b) - rankOf(a))[0]!;
  }
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  return view.trick.length === 0 ? lead(view, legal, k) : follow(view, legal, k);
}

export const pitchBot: BotEngine<PitchView, PitchLegal, PitchAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return this.chooseBet!(view, legal, ctx);
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      const options: PitchAction[] = [...(legal.canPass ? [{ type: 'pass' as const }] : []), ...legal.amounts.map((amount) => ({ type: 'bid' as const, amount }))];
      return options[ctx.rng.int(options.length)]!;
    }
    return bidChoice(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = pitchBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 22));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') return legal.canPass ? { type: 'pass' } : { type: 'bid', amount: legal.amounts[0]! };
    if (legal.kind === 'play') return { type: 'play', card: cheapest(legal.cards, view.trump) };
    throw new Error('No legal action');
  },
};
